import React from 'react';
import type { SiteRagReport } from '../../../../types/siteRag';
import { useEffectiveReportTheme } from '../../../../lib/useEffectiveReportTheme';
import { roleColor } from '../../../../lib/reportRoleColors';
import { GENERIC_SECTION_INSUFFICIENT_MESSAGE } from '../../../../lib/sectionFallbacks/derivedFallbacks';
import { EditorialSubsection, EditorialRecommendations, EditorialCallout } from '../../report/editorial';
import { resolveSectionCallout } from '../../../../lib/reportRendering/reportSectionCallouts';
import { FindingClaimsList } from '../../report/FindingClaimsList';
import { CapacityCreditDeepDiveSection } from '../PowerAvailability/CapacityCreditDeepDiveSection';
import { capacityCreditDeepDiveHasContent } from '../../../../lib/tocEntries';
import { makeRenderCitedText, resolveBlockScale, emptyStateNoteProps } from './feasibilityBodyHelpers';
import { isEditorialLayoutsEnabled } from '../../../../lib/runtimeFlags';
import { hasGeneratedEditorialLayer } from '../../../../lib/editorial/resolveEditorialLayer';
import { EditorialProseBody } from '../../report/editorial/EditorialProseBody';

/**
 * #34 P2 STAGE B — Grid Power Study (feasibility) + COMBINES fold #3.
 *
 * The grid-study body was lifted VERBATIM from the SiteRagReportView inline
 * block so the modern `NewTypeSectionsRouter` can mount it (body only — the
 * router shell owns the anchor, heading and ProvenanceStrip).
 *
 * COMBINES (fold #3) — the firm-capacity (ELCC) deep-dive is now FOLDED INTO
 * this section: beneath the grid assessment grid we render the
 * `capacityCreditDeepDive` content (CapacityCreditDeepDiveSection) under a
 * labelled <h3> sub-head. CapacityCreditDeepDiveSection reads
 * `report.powerAnalysis.capacityCreditDeepDive` (optional) + the deterministic
 * power-basis / firm-deliverable strip, exactly as it did standalone. Its inner
 * SectionShell auto-suppresses its own <h2> (the router's SectionIdContext is
 * `gridStudy`, so `anchorOwnedByRouter` is true) — only its body renders, under
 * our sub-head. No ELCC content is dropped.
 */
export const GridStudySection: React.FC<{ report: SiteRagReport; label?: string }> = ({ report }) => {
  const theme = useEffectiveReportTheme();
  const renderCitedText = makeRenderCitedText(report);
  const blockScale = resolveBlockScale(theme.headingScale);
  // N1 — when the editorial monograph is ON, lift the section's lead generated
  // recommendation into a magazine PULL-QUOTE (the large serif feature quote via
  // the `data-editorial-pullquote` hook, scoped under `[data-report-root]
  // [data-editorial]`). It is REAL content (the first generated grid
  // recommendation), never fabricated, and is GATED on the flag so the element
  // is absent entirely with the flag OFF (byte-stable) rather than rendering as
  // un-styled prose. It is also `data-flow="flow"` so it packs with the prose
  // columns in N2 (the prose register, not an exhibit).
  const editorialOn = isEditorialLayoutsEnabled();
  const leadRecommendation = report.gridStudy?.recommendations?.[0];
  // WAVE 3 — when a GENERATED editorial layer is present the flowing prose body
  // (rendered by the router via EditorialProseBody) already re-voices the
  // interconnection / transmission / capacity assessment, so the superseded
  // labelled EditorialSubsection prose blocks AND the duplicate lead-recommendation
  // pull-quote are DEMOTED — the section keeps only its sparing EXHIBITS
  // (recommendations rail, confirmed risks, ELCC firm-adequacy panel). No layer ⇒
  // all structured blocks render exactly as today (byte-identical).
  const layerPresent = hasGeneratedEditorialLayer(report, 'gridStudy');

  const callout = resolveSectionCallout(report, 'gridStudy');

  // Fold #3 — the ELCC deep-dive renders whenever it carries data. The basis
  // strip resolves from the intent's power stack even before the AI populates
  // the deep-dive object, so the sub-block is rarely empty.
  //
  // EMPTY-SECTION SUPPRESSION (William QA 2026-07-02) — GATE the whole fold on the
  // SAME predicate the body uses. CapacityCreditDeepDiveSection returns `null` when
  // it has no content (`!hasData`), but the folded `<h3>` "Capacity-Credit & Firm
  // Adequacy (ELCC)" heading + its wrapper are STATIC SIBLINGS of that component —
  // so an empty deep-dive left an ORPHAN HEADER WITH NO BODY on the feasibility PDF
  // (the header-no-body defect; the category page confirms "no firm-capacity (ELCC)
  // adequacy snapshot for this report"). `capacityCreditDeepDiveHasContent` mirrors
  // the component's `hasData` EXACTLY and is the same predicate the router's
  // bare-heading skip-guard + the TOC numbering key on, so gating here keeps all
  // four render decisions in lockstep: no content ⇒ no heading, no wasted page. The
  // feasibility sample fixture carries capacityCreditDeepDive data, so the
  // reportSurfaceParity ELCC canary + the fold both still render for it (unchanged).
  const elccSubBlock = !capacityCreditDeepDiveHasContent(report) ? null : (
    // The folded ELCC sub-block keeps a `data-section`/`data-section-id` marker
    // (nested inside the gridStudy shell) so the firm-adequacy content is still
    // discoverable as a section subtree on BOTH the viewer and the export tree
    // — same nesting precedent as `recommendations` inside `actionPlan`. This
    // also preserves the reportSurfaceParity "capacityCreditDeepDive ELCC canary"
    // (the 9c7c7990 PDF-drop guard): the ELCC must never silently vanish from a
    // feasibility PDF, fold or not.
    // N1 — the ELCC deep-dive (instrument panel + per-phase firm-adequacy rows)
    // is a tall sub-block that must BREAK across a page; it is full-width by
    // default in the 1-col editorial body, so no atomic `data-flow="span"`.
    <div className="space-y-2 pt-2" data-section="capacityCreditDeepDive" data-section-id="capacityCreditDeepDive" data-subsection="capacityCreditDeepDive">
      <h3 className="text-[11px] font-semibold uppercase" style={{ fontFamily: 'var(--report-body-font, inherit)', letterSpacing: '0.14em', color: roleColor(theme, 'ink') }}>Capacity-Credit &amp; Firm Adequacy (ELCC)</h3>
      <CapacityCreditDeepDiveSection report={report} label="Capacity-Credit & Firm Adequacy (ELCC)" />
    </div>
  );

  return !report.gridStudy ? (
    // The grid prose is absent — still surface the folded ELCC strip (it draws
    // from the intent power stack, independent of the grid narrative) so no
    // firm-adequacy content is lost when only the grid prose is missing.
    <div className="space-y-4 mt-4">
      {/* N1.5C-flat — graceful-omit note flattens to an italic muted left-rule
          note on the cream page in editorial mode (shared seam); flag OFF keeps
          the legacy boxed note byte-identical. */}
      <div {...emptyStateNoteProps(editorialOn, theme, false)} data-testid="gridStudy-empty">
        {GENERIC_SECTION_INSUFFICIENT_MESSAGE}
      </div>
      {elccSubBlock}
    </div>
  ) : (
    <div className="space-y-6 mt-4">
      {/* WAVE 3 — flowing editorial PROSE BODY (drop-cap + pull-quote) when a
          generated layer is present; the labelled assessment blocks below are
          suppressed in tandem so the prose is not duplicated. */}
      {layerPresent && <EditorialProseBody report={report} sectionId="gridStudy" theme={theme} />}
      {/* GC golden callout — grid queue / hosting-capacity caveat, GROUNDED in the
          measured grid dataset (queue-time estimate and/or 'Limited'/'Constrained'
          capacity rating). Fires only when one of those real signals is present;
          clean grid data renders nothing. */}
      {callout && (
        <EditorialCallout theme={theme} tone={callout.tone} label={callout.label} style={{ marginTop: 8 }}>
          {callout.body}
        </EditorialCallout>
      )}
      {/* This block IS an internal two-column grid (assessment
          column + recommendations/risks column). Under a two-column
          body preset it would otherwise be squeezed into ONE of the
          wrapper's two flow columns, fighting column-count. Mark it
          [data-span-columns] so it spans both flow columns and reads
          as a clean full-width exhibit (its own 2-col grid intact)
          instead of a torn half-width grid. No-op in single-column. */}
      {/* N1 — in the editorial monograph, make the 2-col assessment GRID atomic so
          it never splits mid-row across a page (a fragmented CSS grid desynced its
          column tracks → overlap). Gated so flag-OFF is byte-stable; the grid is <
          one page, so a clean whole-grid break is safe. */}
      <div data-span-columns style={editorialOn ? { breakInside: 'avoid' } : undefined} className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left column — assessment sub-blocks routed through the
            theme-adaptive EditorialSubsection (editorial = hairline-top
            prose flow, technical = thin left rule, compact = dense top
            hairline). The heavy `border border-border` cards are gone.
            P-GROUP Phase A (`row-group`) — an inline-flex COLUMN is a single
            unfragmentable box for pagedjs, so a tall stack was pushed WHOLE to
            the next sheet (stranding the sub-head above it + a blank tail).
            The deck's row-group rule flattens it to fragmentable block flow;
            each EditorialSubsection inside is its own bounded head-body atom.
            Screen/exports keep the flex+gap (the rule is deck-scoped). */}
        <div data-layout-group="row-group" style={{ display: 'flex', flexDirection: 'column', gap: blockScale === 'compact' ? 10 : 16 }}>
          {/* N1 — the LEAD prose block of the first feasibility study section
              carries the editorial drop-cap (raised serif initial). The marker
              is inert unless the editorial flag is ON (scoped CSS), so this is
              byte-stable with the flag OFF.
              WAVE 3 — SUPPRESSED when a generated layer is present (the flowing
              prose body re-voices these three assessments). */}
          {!layerPresent && (
            <>
              <EditorialSubsection theme={theme} label="Interconnection Assessment" leadCap>{renderCitedText(report.gridStudy.interconnectionAssessment)}</EditorialSubsection>
              <EditorialSubsection theme={theme} label="Transmission Access">{renderCitedText(report.gridStudy.transmissionAccess)}</EditorialSubsection>
              <EditorialSubsection theme={theme} label="Capacity Analysis">{renderCitedText(report.gridStudy.capacityAnalysis)}</EditorialSubsection>
            </>
          )}
          {layerPresent && (
            <EditorialSubsection theme={theme} label="Capacity Analysis">{renderCitedText(report.gridStudy.capacityAnalysis)}</EditorialSubsection>
          )}
        </div>
        {/* Right column — recommendations via the accent-LEFT-rule
            EditorialRecommendations (no boxed accent card) + confirmed risks.
            P-GROUP Phase A (`row-group`) — fragmentable in the deck (see left
            column note): a tall recs+risks stack fills the page tail instead of
            being pushed whole. */}
        <div data-layout-group="row-group" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <EditorialRecommendations
            theme={theme}
            items={((report.gridStudy?.recommendations) || []).map((rec) => renderCitedText(rec))}
          />
          {(report.gridStudy?.risks?.length ?? 0) > 0 && (
            <FindingClaimsList
              entries={report.gridStudy?.risks ?? []}
              references={report.references}
              siteId={report.siteId}
              reportId={report.id}
              sectionId="gridStudy"
              responses={report.openQuestionResponses}
              redFlagsHeading="Grid risks (confirmed)"
              className="space-y-4"
            />
          )}
        </div>
      </div>
      {/* N1 — editorial PULL-QUOTE (gated; REAL lead recommendation).
          WAVE 3 — suppressed when a generated layer is present: the layer-driven
          EditorialProseBody emits the section pull-quote, so this section-local
          one would duplicate it. */}
      {!layerPresent && editorialOn && typeof leadRecommendation === 'string' && leadRecommendation.trim().length > 0 && (
        <blockquote data-editorial-pullquote data-flow="flow">
          {renderCitedText(leadRecommendation)}
        </blockquote>
      )}
      {/* COMBINES fold #3 — firm-capacity (ELCC) deep-dive, folded beneath the
          grid assessment under its own sub-head. */}
      {elccSubBlock}
    </div>
  );
};
